import React, { useState, useMemo } from 'react';
import { ChevronDown, Plus, Trash2, Flower2, Sparkles, Calculator } from 'lucide-react';

const FLOWER_CATEGORIES = [
  { id: 'bouquet', label: 'Bridal bouquet', emoji: '\uD83D\uDC90' },
  { id: 'bridesmaids', label: 'Bridesmaidbuketter', emoji: '\uD83C\uDF38' },
  { id: 'boutonnieres', label: 'Knappehullsblomster', emoji: '\uD83C\uDF3A' },
  { id: 'corsages', label: 'Korsasjer', emoji: '\uD83C\uDF3C' },
  { id: 'centerpieces', label: 'Borddekorasjon', emoji: '\uD83C\uDF3B' },
  { id: 'aisle', label: 'Gangarrangement', emoji: '\uD83D\uDEB6' },
  { id: 'altar', label: 'Alter / Ceremony', emoji: '\u26EA' },
  { id: 'welcome', label: 'Velkomstomr\u00e5de', emoji: '\uD83D\uDEAA' },
  { id: 'bar', label: 'Bar-blomster', emoji: '\uD83C\uDF78' },
  { id: 'cake', label: 'Kakeblomster', emoji: '\uD83C\uDF82' },
  { id: 'backdrop', label: 'Bakgrunn / Fotoareal', emoji: '\uD83D\uDCF8' },
  { id: 'details', label: 'Sm\u00e5detaljer & Dekor', emoji: '\u2728' },
];

const SEASON_SUGGESTIONS = {
  spring: { label: 'Spring', flowers: 'Tulips, peonies, ranunculus, lily of the valley, freesia, cherry blossom, anemones, hyacinth' },
  summer: { label: 'Summer', flowers: 'Roses, dahlias, sunflowers, lavender, hydrangea, peonies, delphinium, lisianthus' },
  autumn: { label: 'Autumn', flowers: "Chrysanthemums, dahlias, sunflowers, baby's breath, hypericum, heather, eucalyptus" },
  winter: { label: 'Winter', flowers: 'Christmas roses, amaryllis, white roses, ranunculus, anemone, eucalyptus, mistletoe, poinsettia' },
  '': { label: 'Not selected', flowers: 'Roses, peonies, ranunculus, eucalyptus, hydrangea, dahlias' },
};

const emptyEntry = () => ({
  flowers: '', colors: '', quantity: '', style: '',
  vendor: '', estimatedPrice: '', actualPrice: '', notes: '',
});

const formatCurrency = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? '' : num.toLocaleString('en-US');
};

export const FlowerPlanner = ({ flowers, onChange, formData }) => {
  const [openSections, setOpenSections] = useState(['bouquet']);

  const data = flowers || {};
  const season = formData?.season || '';

  const toggleSection = (id) => {
    setOpenSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const updateCategory = (catId, updates) => {
    onChange({ ...data, [catId]: { ...(data[catId] || emptyEntry()), ...updates } });
  };

  const seasonInfo = SEASON_SUGGESTIONS[season] || SEASON_SUGGESTIONS[''];

  // Budget summary
  const totals = useMemo(() => {
    let estimated = 0, actual = 0, filled = 0;
    FLOWER_CATEGORIES.forEach(cat => {
      const entry = data[cat.id];
      if (entry?.flowers?.trim()) filled++;
      const est = parseFloat(entry?.estimatedPrice);
      const act = parseFloat(entry?.actualPrice);
      if (!isNaN(est)) estimated += est;
      if (!isNaN(act)) actual += act;
    });
    return { estimated, actual, filled };
  }, [data]);

  return (
    <div data-testid="flower-planner" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Flower planner</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">
            Planlegg blomster for hver del av the wedding
          </p>
        </div>
        <div className="text-right text-[10px] font-sans">
          <span className="text-stone-500">{totals.filled} / {FLOWER_CATEGORIES.length} kategorier</span>
        </div>
      </div>

      {/* Season suggestion */}
      <div className="bg-white rounded-xl border border-stone-200/80 p-3" data-testid="season-suggestion">
        <div className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[10px] font-sans font-bold text-stone-600 mb-0.5">
              {season ? `Blomsterforslag for ${seasonInfo.label.toLowerCase()}sesongen` : 'Popular flower choices for weddings'}
            </p>
            <p className="text-[10px] font-sans text-stone-500 leading-relaxed">{seasonInfo.flowers}</p>
          </div>
        </div>
      </div>

      {/* Budget summary */}
      {(totals.estimated > 0 || totals.actual > 0) && (
        <div className="bg-white rounded-xl border border-stone-200/80 p-3 flex items-center gap-4" data-testid="flower-budget">
          <Calculator className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
          <div className="flex-1 flex items-center gap-4 text-[10px] font-sans">
            <div>
              <span className="text-stone-400">Estimert: </span>
              <span className="font-bold text-stone-700">{formatCurrency(totals.estimated)} kr</span>
            </div>
            {totals.actual > 0 && (
              <div>
                <span className="text-stone-400">Faktisk: </span>
                <span className={`font-bold ${totals.actual > totals.estimated ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(totals.actual)} kr
                </span>
              </div>
            )}
            {totals.actual > 0 && totals.estimated > 0 && (
              <div>
                <span className="text-stone-400">Differanse: </span>
                <span className={`font-bold ${totals.actual - totals.estimated > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {totals.actual - totals.estimated > 0 ? '+' : ''}{formatCurrency(totals.actual - totals.estimated)} kr
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-2" data-testid="flower-categories">
        {FLOWER_CATEGORIES.map(cat => {
          const isOpen = openSections.includes(cat.id);
          const entry = data[cat.id] || emptyEntry();
          const hasFill = entry.flowers?.trim() || entry.vendor?.trim();

          return (
            <div key={cat.id} className={`rounded-xl border transition-all ${
              hasFill ? 'border-primary/20 bg-primary/[0.02]' : 'border-stone-200/80 bg-white'
            }`} data-testid={`flower-cat-${cat.id}`}>
              {/* Section header */}
              <button onClick={() => toggleSection(cat.id)}
                className="flex items-center w-full px-4 py-2.5 text-left gap-3" data-testid={`flower-toggle-${cat.id}`}>
                <span className="text-sm">{cat.emoji}</span>
                <span className="flex-1 text-[11px] font-sans font-semibold text-stone-700">{cat.label}</span>
                {hasFill && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                {entry.estimatedPrice && (
                  <span className="text-[9px] font-sans text-stone-400">{formatCurrency(entry.estimatedPrice)} kr</span>
                )}
                <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Section content */}
              {isOpen && (
                <div className="px-4 pb-3 space-y-2.5 border-t border-stone-100 pt-2.5">
                  {/* Row 1: Blomster + Farger */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Blomster</label>
                      <input value={entry.flowers} onChange={e => updateCategory(cat.id, { flowers: e.target.value })}
                        placeholder="e.g. roser, pioner, eukalyptus"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Farger</label>
                      <input value={entry.colors} onChange={e => updateCategory(cat.id, { colors: e.target.value })}
                        placeholder="e.g. blush, white, green"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                  </div>

                  {/* Row 2: Mengde + Stil */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Mengde</label>
                      <input value={entry.quantity} onChange={e => updateCategory(cat.id, { quantity: e.target.value })}
                        placeholder="e.g. 1 bukett, 10 table"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Stil</label>
                      <input value={entry.style} onChange={e => updateCategory(cat.id, { style: e.target.value })}
                        placeholder="e.g. loose, structured, cascade"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                  </div>

                  {/* Row 3: Vendor */}
                  <div>
                    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Vendor</label>
                    <input value={entry.vendor} onChange={e => updateCategory(cat.id, { vendor: e.target.value })}
                      placeholder="Name of flower shop/florist"
                      className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                  </div>

                  {/* Row 4: Priceer */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Estimated price (kr)</label>
                      <input type="number" value={entry.estimatedPrice} onChange={e => updateCategory(cat.id, { estimatedPrice: e.target.value })}
                        placeholder="0"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Actual price (kr)</label>
                      <input type="number" value={entry.actualPrice} onChange={e => updateCategory(cat.id, { actualPrice: e.target.value })}
                        placeholder="0"
                        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                    </div>
                  </div>

                  {/* Row 5: Notes */}
                  <div>
                    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">Notes</label>
                    <textarea value={entry.notes} onChange={e => updateCategory(cat.id, { notes: e.target.value })}
                      placeholder="Ekstra informasjon, spesielle wishr..."
                      rows={2}
                      className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none resize-none" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
